import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "react-lazy-load-image-component/src/effects/blur.css";
import ViatorAwardBadge from "./viatorBadge";
import TopRatedExperience from "../topRatedExperience";
import {
  getCoverImageSources,
  roundRatingToTwoDecimal,
  formatReviewCount,
} from "../experienceUtils";
import placeholderImage from "@/assets/imgloading.png";
import { ViatorProductInitial } from "@/types";
import LazyLoad from "../../image/LazyLoad";
import { formatPrice, formatImageUrl } from "@/lib/general";
import { HeartIcon } from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import logger from "@/utils/logger";
const truncateTitle = (title: string, maxLength: number) => {
  if (title.length > maxLength) {
    return title.slice(0, maxLength) + "...";
  }
  return title;
};

interface ViatorItemProps {
  product: ViatorProductInitial;
  isLiked: boolean;
  setViatorProduct: (product: ViatorProductInitial) => void;
  setIsLiked: (experienceId: string) => void;
  handleOpenModal: (placeId: string) => void;
  addToItinerary: (product: ViatorProductInitial) => void;
  fetchingProduct: boolean;
  isAdmin: boolean;
}

export const ViatorItem: React.FC<ViatorItemProps> = ({
  product,
  isLiked,
  setViatorProduct,
  setIsLiked,
  handleOpenModal,
  addToItinerary,
  fetchingProduct,
  isAdmin,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isAwardHovered, setIsAwardHovered] = useState(false);
  const [lowResLoaded, setLowResLoaded] = useState(false);

  const awardType = product.types.find((type: string) =>
    /Viator Experience Award \d{4}/.test(type)
  );

  const handleMoreInfoClick = () => {
    logger.log("More info clicked:", product);
    setViatorProduct(product);
    handleOpenModal(product.product_code);
  };

  const handleAddToDay = () => {
    addToItinerary(product);
  };

  // get image sources
  const imageSources = getCoverImageSources(product.images);
  const lowRes = imageSources?.lowRes || placeholderImage;
  const highRes = imageSources?.highRes || placeholderImage;

  useEffect(() => {
    const lowResImg = new Image();
    lowResImg.src = lowRes || placeholderImage;
    lowResImg.onload = () => setLowResLoaded(true);
  }, [lowResLoaded, lowRes]);

  const handleLike = async () => {
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 3000);
    setIsLiked(product.product_code);
  };

  return (
    <div
      key={product.product_code}
      className="experienceItem flex w-full flex-col items-start rounded-md transition duration-300 ease-in-out"
    >
      <div className="relative w-full group">
        <button
          onClick={handleMoreInfoClick}
          className="rounded-md h-48 w-full overflow-hidden relative"
        >
          <LazyLoad
            src={formatImageUrl(highRes, "high")}
            lowResSrc={formatImageUrl(lowRes, "low")}
            alt={product.title}
            className="w-full h-full object-cover"
          />
        </button>

        <div
          className="inset-0"
          onMouseEnter={() => setIsAwardHovered(true)}
          onMouseLeave={() => setIsAwardHovered(false)}
        >
          {awardType ? (
            <ViatorAwardBadge
              awardType={awardType}
              isHovered={isAwardHovered}
            />
          ) : (
            <TopRatedExperience
              rating={product.reviews.combinedAverageRating}
              totalReviews={product.reviews.totalReviews}
              isHovered={isAwardHovered}
            />
          )}
        </div>

        <div className="absolute top-2 left-2 flex flex-row items-center space-x-1 bg-white/80 backdrop-blur-md rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out">
          <p className="text-xs font-light text-neutral-900">
            {formatPrice(product.currency, product.from_price)}
          </p>
        </div>
        <div className="group z-10 absolute right-3 top-2 flex items-center justify-center bg-stone-100 w-[27px] h-[27px] rounded-full">
          <button
            onClick={handleLike}
            className="group relative flex items-center justify-center w-full h-full"
          >
            <AnimatePresence>
              {isLiked && (
                <motion.i
                  key="filled-heart"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="text-[14px] text-red-600 absolute"
                >
                  <HeartIconSolid className="w-4 h-4" />
                </motion.i>
              )}
            </AnimatePresence>
            <motion.div
              animate={isLiked ? { scale: 0 } : { scale: 1 }}
              className={`${
                isLiked ? "text-transparent" : "text-gray-600"
              } transition-colors duration-300`}
            >
              <HeartIcon className="w-4 h-4" />
            </motion.div>
            {isAnimating && (
              <motion.div
                initial={{ scale: 0.8, opacity: 1 }}
                animate={{ scale: 1.3, opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 bg-red-500/75 rounded-full"
              />
            )}
          </button>
        </div>

        <AnimatePresence>
          {isAnimating && (
            <div className="absolute right-3 top-2 w-[35px] h-[35px] overflow-visible pointer-events-none">
              {[...Array(5)].map((_, index) => (
                <motion.i
                  key={index}
                  initial={{
                    opacity: 1,
                    scale: 0,
                    x: "50%",
                    y: "50%",
                  }}
                  animate={{
                    opacity: 0,
                    scale: 1.5,
                    x: `${100 + (Math.random() - 0.5) * 100}%`,
                    y: `${-50 - Math.random() * 100}%`,
                  }}
                  transition={{
                    duration: 1.5 + Math.random() * 1,
                    ease: "easeOut",
                    opacity: { duration: 2 },
                  }}
                  className="fa-solid fa-heart text-red-600 text-sm absolute"
                  style={{
                    originX: "50%",
                    originY: "50%",
                  }}
                />
              ))}
            </div>
          )}
        </AnimatePresence>
      </div>
      <div className="flex w-full flex-row items-center justify-between pt-0.5 pb-3">
        <div
          className={`flex flex-col items-start justify-start space-y-1 w-full ${
            isAwardHovered ? "opacity-50" : "opacity-100"
          }`}
        >
          <div className="w-full">
            <h3 className="experience-name-text text-[15px] w-full font-[500] tracking-tight text-neutral-800">
              {truncateTitle(product.title, 32)}
            </h3>
          </div>
          <div className="w-full flex flex-row items-center justify-start space-x-2 pr-1">
            <div className="flex flex-row items-center space-x-1.5 w-full pt-1">
              <div className="py-1 bg-indigo-50 flex flex-row items-center space-x-2 px-2 text-indigo-600 rounded-md ">
                <i
                  className={`fa-solid fa-star text-indigo-600 text-[12px] -translate-y-[0.5px]`}
                ></i>
                <p className="experience-name-text text-[13px] font-light text-indigo-600 truncate whitespace-nowrap">
                  {roundRatingToTwoDecimal(
                    product.reviews.combinedAverageRating
                  )}
                </p>
              </div>
              <p className="pl-0.5 experience-name-text text-[12px] font-light text-neutral-500 truncate whitespace-nowrap">
                {formatReviewCount(product.reviews.totalReviews)} reviews
              </p>
            </div>
            {isAdmin && (
              <button
                onClick={handleAddToDay}
                className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-indigo-800 hover:bg-indigo-700 font-medium transition-all duration-500 ease-in-out text-white"
              >
                {fetchingProduct ? (
                  <i className="fa-solid fa-circle-notch text-xs animate-spin"></i>
                ) : (
                  <i className="fa-solid fa-plus text-xs"></i>
                )}
              </button>
            )}
            {/* more info */}
            <button
              onClick={handleMoreInfoClick}
              className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-neutral-100 hover:bg-neutral-200 font-medium transition-all duration-500 ease-in-out text-neutral-800"
            >
              <p className="text-xs font-bold italic transition-transform duration-300 ease-in-out">
                i
              </p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
